import React, { useContext, createContext, useState, useEffect } from 'react';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { noop } from 'lodash';
import { Instance } from 'pspdfkit';
import useRouter from '../../utils/useRouter';
import { useSelector } from 'react-redux';
import { selectAdjacentSiblings, selectMindItem } from '../../reducers/mindItems.selectors';
import { selectIsItemInOwnCollections } from '../../reducers/collections.selectors';
import useAction from '../../utils/useAction';
import { fetchMindItem as fetchMindItemAction } from '../../actions/mindItems.actions';
import useScreenSize from '../../utils/useScreenSize';
import { useNewDesign } from '../../state/DesignPreferenceContextProvider';
import useOnboardingStepCounter from '../../utils/onboardingStepCounter';
import { Alert } from '../NotificationAlert/NotificationAlert';
import { selectUser } from '../../reducers/user.selectors';

const defaultZoomFactor = 100;

interface ReaderContext {
  mindItemId: string;
  isReadOnly: boolean;
  previousSibling?: MindItem;
  nextSibling?: MindItem;
  isPinned: boolean;
  isInCollection: boolean;
  isTextBased: boolean;
  onPageLoad: () => void;
  pagesLoaded: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  initialPage: number;
  setInitialPage: (page: number) => void;
  hasAttachments: boolean;
  isReaderDetailsCollapsed?: boolean;
  setReaderDetailsCollapsed?: (isVisible: boolean) => void;
  pspdfkitInstance?: Instance;
  setPspdfkitInstance?: (instance: Instance | undefined) => void;
  showNewReader?: boolean;
  zoomProperties: {
    zoomFactor: number;
    setZoomFactor: (factor: number) => void;
    baselineZoomFactor: number;
    setBaselineZoomFactor: (factor: number) => void;
  },
  fullScreenMode?: boolean;
  setFullScreenMode?: (mode: boolean) => void;
  onboardingStepCounter?: ReturnType<typeof useOnboardingStepCounter>;
  alert?: Alert;
  setAlert?: (alert: Alert | undefined) => void;
  onboardingStepCounterMobile?: ReturnType<typeof useOnboardingStepCounter>;
}

export const ReaderContext = createContext<ReaderContext>({
  mindItemId: '',
  isReadOnly: false,
  previousSibling: undefined,
  nextSibling: undefined,
  currentPage: 0,
  setCurrentPage: noop,
  isPinned: false,
  isInCollection: false,
  isTextBased: false,
  onPageLoad: noop,
  pagesLoaded: false,
  initialPage: 1,
  setInitialPage: noop,
  hasAttachments: false,
  isReaderDetailsCollapsed: false,
  setReaderDetailsCollapsed: noop,
  pspdfkitInstance: undefined,
  setPspdfkitInstance: noop,
  showNewReader: false,
  zoomProperties: {
    zoomFactor: defaultZoomFactor,
    setZoomFactor: noop,
    baselineZoomFactor: defaultZoomFactor,
    setBaselineZoomFactor: noop,
  },
  fullScreenMode: false,
  setFullScreenMode: noop,
  onboardingStepCounter: undefined,
  alert: undefined,
  setAlert: noop,
  onboardingStepCounterMobile: undefined,
});

const ReaderContextProvider : React.FC = (props) => {
  const onboardingStepCounterDesktop = useOnboardingStepCounter();
  const onboardingStepCounterMobile = useOnboardingStepCounter();
  const [alert, setAlert] = useState<Alert | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [initialPage, setInitialPage] = useState(1);
  const [zoomFactor, setZoomFactor] = useState(defaultZoomFactor);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [baselineZoomFactor, setBaselineZoomFactor] = useState(defaultZoomFactor);
  const [loadedPages, setLoadedPages] = useState<number>(0);
  const [isReaderDetailsCollapsed, setReaderDetailsCollapsed] = useState<boolean>(false);
  const [pspdfkitInstance, setPspdfkitInstance] = useState<Instance | undefined>(undefined);
  const { isLargeScreen } = useScreenSize();
  const { prefersNewDesign } = useNewDesign();

  const fetchMindItem = useAction(fetchMindItemAction);
  const user = useSelector(selectUser);

  const { match } = useRouter();
  const { params: { documentId } } = match;

  const mindItem = useSelector(state => selectMindItem(state, documentId));
  const isInCollection = useSelector(state => selectIsItemInOwnCollections(state, documentId));

  const adjacentSiblings = useSelector(state => selectAdjacentSiblings(state, { id: documentId, type: 'mindItem' }));

  useEffect(() => {
    setLoadedPages(0);
    fetchMindItem(documentId);
    setZoomFactor(defaultZoomFactor);
    setBaselineZoomFactor(defaultZoomFactor);
  }, [documentId]);
  const nextSibling = adjacentSiblings?.next?.mindItem;
  const previousSibling = adjacentSiblings?.previous?.mindItem;

  const handlePageLoad = () => {
    setLoadedPages(loadedPages + 1);
  };

  const showNewReader = prefersNewDesign
    && user?.hasDocumentLevelAccess
    && mindItem?.fileType !== 'video';

  if (!mindItem) {
    return null;
  }

  return <ReaderContext.Provider
    value={{
      currentPage: currentPage,
      setCurrentPage: setCurrentPage,
      initialPage: initialPage,
      setInitialPage: setInitialPage,
      mindItemId: mindItem.id,
      isReadOnly: mindItem.isReadOnly,
      previousSibling: previousSibling,
      nextSibling,
      isPinned: mindItem.isPinned,
      isInCollection: isInCollection,
      isTextBased: mindItem.isTextBased,
      onPageLoad: handlePageLoad,
      pagesLoaded: mindItem.fileType === 'document' && loadedPages >= mindItem.pages.length,
      hasAttachments: mindItem.hasAttachments,
      isReaderDetailsCollapsed,
      setReaderDetailsCollapsed,
      showNewReader,
      pspdfkitInstance,
      setPspdfkitInstance,
      zoomProperties: {
        zoomFactor: zoomFactor,
        setZoomFactor: setZoomFactor,
        baselineZoomFactor: baselineZoomFactor,
        setBaselineZoomFactor: setBaselineZoomFactor,
      },
      fullScreenMode: fullScreenMode && isLargeScreen,
      setFullScreenMode,
      onboardingStepCounter: onboardingStepCounterDesktop,
      onboardingStepCounterMobile: onboardingStepCounterMobile,
      alert,
      setAlert,
    }}
  >
    {props.children}
  </ReaderContext.Provider>;
};

export const useReaderContext = () => useContext(ReaderContext);

export default ReaderContextProvider;
